/**
 * tunnelPipeline — tunnel of light rings receding into infinity.
 *
 * A 3D tunnel effect: rings stretch away from the camera, each one
 * further and smaller, creating the sensation of flying through a
 * wormhole. Each ring is a colored band with audio-reactive brightness.
 *
 * The camera moves forward (z increases). The ring at depth z has
 * screen radius proportional to 1/(z - cameraZ). The color shifts
 * with depth (purple → cyan → yellow).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TunnelPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}


void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Polar coords.
  float r = length(p);
  float theta = atan(p.y, p.x);

  // Tunnel: rings are at depths z = 1.0, 2.0, 3.0, ...
  // For a pixel at radius r, the depth that hits the ring is z = 1/r.
  // So depth is a function of r alone.
  float depth = 1.0 / max(r, 0.01);
  // Camera moves forward over time (audio speeds it up).
  float camZ = uTime * (2.0 + bass * 4.0);
  float worldZ = depth + camZ;
  // Ring indices: which ring are we closest to?
  float ringIdx = floor(worldZ);
  // Position within the ring: fractional part of worldZ (0..1).
  float ringPhase = fract(worldZ);

  // Each ring has a color that cycles through the rainbow.
  float hue = ringIdx * 0.15 + uTime * 0.1;
  vec3 ringColor = vec3(
    0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
    0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
    0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
  );
  // Audio: shift palette with mid.
  ringColor = mix(ringColor, ringColor.gbr, mid * 0.5);

  // Ring brightness falls off with depth (further rings dimmer).
  float depthFade = exp(-depth * 0.15);
  // Ring intensity based on ringPhase (bright at center of ring).
  float ringIntensity = sin(ringPhase * 3.14159);  // sin for a smooth peak
  // Audio: bass boosts ring intensity.
  ringIntensity *= 1.0 + bass * 1.5;
  // Combine.
  vec3 col = ringColor * ringIntensity * depthFade;

  // Add a center glow.
  col += ringColor * exp(-r * 3.0) * 0.8;

  // Background: deep dark space.
  vec3 bg = vec3(0.01, 0.01, 0.04);
  bg += vec3(0.05, 0.05, 0.15) * exp(-r * 2.0) * 0.3;
  col = mix(bg, col, clamp(length(col), 0.0, 1.0));

  // Add high-frequency sparkle at certain ring phases.
  float sparkle = step(0.95, fract(ringPhase + hash21(vec2(floor(worldZ), floor(theta * 8.0))) * 1.7));
  col += vec3(1.0, 0.9, 0.7) * sparkle * depthFade * high * 1.5;

  // Vignette.
  float vig = 1.0 - r * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeTunnelBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeTunnelRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTunnelPipeline(gl: WebGL2RenderingContext): TunnelPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTunnel(
  gl: WebGL2RenderingContext,
  pipe: TunnelPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
